// src/lib/nutritionalAnalysisStorage.ts

import { Assessment } from '@/services/dietaryService';

interface NutritionalAnalysisCache {
  [recipeId: string]: {
    analysis: Assessment;
    timestamp: number;
    version: string;
  };
}

export class NutritionalAnalysisStorage {
  private static readonly MAX_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly KEY = 'cheflab_nutritional_analysis';
  private static readonly VERSION = '1.0.0';

  static getCache(): NutritionalAnalysisCache {
    try {
      const data = localStorage.getItem(this.KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }

  static getAnalysis(recipeId: string): Assessment | null {
    try {
      const cache = this.getCache();
      const entry = cache[recipeId];
      
      if (!entry) return null;
      
      // Check version compatibility
      if (entry.version !== this.VERSION) {
        this.removeAnalysis(recipeId);
        return null;
      }
      
      return entry.analysis;
    } catch {
      return null;
    }
  }

  static saveAnalysis(recipeId: string, analysis: Assessment): void {
    try {
      const cache = this.getCache();
      const entry = {
        analysis,
        timestamp: Date.now(),
        version: this.VERSION
      };
      
      cache[recipeId] = entry;
      
      // Check size and clean up if needed
      if (this.getSize() > this.MAX_SIZE) {
        this.cleanup();
      }
      
      localStorage.setItem(this.KEY, JSON.stringify(cache));
    } catch {
    }
  }

  static removeAnalysis(recipeId: string): void {
    try {
      const cache = this.getCache();
      delete cache[recipeId];
      localStorage.setItem(this.KEY, JSON.stringify(cache));
    } catch {
    }
  }

  static clearAll(): void {
    try {
      localStorage.removeItem(this.KEY);
    } catch {
    }
  }

  private static cleanup(): void {
    try {
      const cache = this.getCache();
      const entries = Object.entries(cache);
      
      if (entries.length === 0) return;
      
      // Sort by timestamp (oldest first)
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      // Remove oldest 25% of entries
      const removeCount = Math.ceil(entries.length * 0.25);
      for (let i = 0; i < removeCount; i++) {
        delete cache[entries[i][0]];
      }
      
      localStorage.setItem(this.KEY, JSON.stringify(cache));
    } catch {
    }
  }

  private static getSize(): number {
    try {
      const data = localStorage.getItem(this.KEY);
      return data ? new Blob([data]).size : 0;
    } catch {
      return 0;
    }
  }

  static getCacheInfo(): {
    totalEntries: number;
    totalSize: number;
    maxSize: number;
    usagePercentage: number;
  } {
    const cache = this.getCache();
    const entries = Object.keys(cache);
    const size = this.getSize();
    
    return {
      totalEntries: entries.length,
      totalSize: size,
      maxSize: this.MAX_SIZE,
      usagePercentage: Math.round((size / this.MAX_SIZE) * 100)
    };
  }
}